import {
  applySnapshot,
  getSnapshot,
  Instance,
  SnapshotIn,
  types,
} from 'mobx-state-tree';
import { LoadingState, LoadingStates } from 'globals/types';
import { temporaryEndpoints } from 'http/endpoints';
import { isStoreInitialized, performSetAction } from 'models/utils/utils';
import {
  BillingDetails,
  BillingDetailsProps,
} from './billing-details/billing-details';

interface BillingDetailsRowProps {
  name: string;
  value: string;
}

export interface BillingInfoProps {
  data?: BillingDetailsProps;
  status: LoadingState;
}

export const BillingInfo = types
  .model({
    data: types.optional(BillingDetails, {}),
    status: types.optional(
      types.enumeration(LoadingStates),
      LoadingState.INITIAL
    ),
  })
  .actions((self) => ({
    set(props: BillingInfoProps): void {
      applySnapshot(self, {
        ...getSnapshot(self),
        ...props,
      });
    },
    updateStatus(status: LoadingState): void {
      applySnapshot(self, {
        ...getSnapshot(self),
        status,
      });
    },
    setData(props: Partial<BillingDetailsProps>): void {
      applySnapshot(self, {
        ...getSnapshot(self),
        data: { ...self.data, ...props },
      });
    },
  }))
  .actions((self) => ({
    async setStore(): Promise<void> {
      await performSetAction({
        store: self,
        endpoint: temporaryEndpoints.billings.billingInfo,
      });
    },
  }))
  .actions((self) => ({
    async init(): Promise<void> {
      const { status } = getSnapshot(self);
      if (isStoreInitialized(status)) return;
      await self.setStore();
    },
    async update(): Promise<void> {
      await self.setStore();
    },
  }))
  .views((self) => ({
    getPaymentDetailsArray(): BillingDetailsRowProps[] {
      const { data } = self;
      const snap = getSnapshot(data);
      return Object.entries(snap).map(([name, value]) => ({
        name,
        value,
      }));
    },
    getBillingDetailsSnapshot(): BillingDetailsProps {
      const { data } = self;
      return getSnapshot(data);
    },
  }));

export const billingInfo = BillingInfo.create();
export type BillingInfoSnapshot = SnapshotIn<typeof BillingInfo>;
export type BillingInfoInstance = Instance<typeof BillingInfo>;
export default billingInfo;
